import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	INCENTIVE_M1_KPI_NAME,
	INCENTIVE_M2_KPI_NAME,
	KPIThresholdConfig,
	SHIFT_REPORT_CONFIG_INITIAL_VALUES,
	ShiftReportConfig,
	TIME_LEFT_KPI_NAME,
} from "../models/ShiftReportConfig";
import { MILL_1 } from "@/setup/mill-config/mill-config";

export const getShiftReportConfig = async (
	params: {
		defaultAreaId?: string | number | null;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		Number(params.defaultAreaId) === MILL_1.id
			? "[M1].[GetShiftReportConfig]"
			: "[M2].[GetShiftReportConfig]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const thresholdRows = get(
			resp,
			"data.tables[0].rows",
			[],
		) as KPIThresholdConfig[];

		return {
			Thresholds:
				thresholdRows.length > 0
					? thresholdRows
					: SHIFT_REPORT_CONFIG_INITIAL_VALUES.Thresholds,
		};
	}

	throw new Error(resp.message || "Error fetching shift report configuration");
};

export const upsertShiftReportConfig = async (params: {
	defaultAreaId?: string | number | null;
	config: ShiftReportConfig;
}): Promise<void> => {
	const apiService = new MESApiService();

	const findKPI = (name: string): KPIThresholdConfig => {
		const fallback =
			SHIFT_REPORT_CONFIG_INITIAL_VALUES.Thresholds.find(
				(t) => t.Name === name,
			) ?? SHIFT_REPORT_CONFIG_INITIAL_VALUES.Thresholds[0];
		return params.config.Thresholds.find((t) => t.Name === name) ?? fallback;
	};

	const tph = findKPI("TPH");
	const uptime = findKPI("Uptime");
	const pushRate = findKPI("PushRate");
	const productivity = findKPI("Productivity");
	const timeLeft = findKPI(TIME_LEFT_KPI_NAME);
	const isMill1 = Number(params.defaultAreaId) === MILL_1.id;
	const incentive = findKPI(
		isMill1 ? INCENTIVE_M1_KPI_NAME : INCENTIVE_M2_KPI_NAME,
	);
	const incentivePrefix = isMill1 ? "IncentiveM1" : "IncentiveM2";

	const parameters: spParameter[] = [
		{ name: "TPH_Red", value: tph.Red },
		{ name: "TPH_Orange", value: tph.Orange },
		{ name: "TPH_Yellow", value: tph.Yellow },
		{ name: "TPH_GreenSoft", value: tph.GreenSoft },
		{ name: "TPH_GreenNormal", value: tph.GreenNormal },
		{ name: "TPH_GreenHard", value: tph.GreenHard },
		{ name: "Uptime_Red", value: uptime.Red },
		{ name: "Uptime_Orange", value: uptime.Orange },
		{ name: "Uptime_Yellow", value: uptime.Yellow },
		{ name: "Uptime_GreenSoft", value: uptime.GreenSoft },
		{ name: "Uptime_GreenNormal", value: uptime.GreenNormal },
		{ name: "Uptime_GreenHard", value: uptime.GreenHard },
		{ name: "PushRate_Red", value: pushRate.Red },
		{ name: "PushRate_Orange", value: pushRate.Orange },
		{ name: "PushRate_Yellow", value: pushRate.Yellow },
		{ name: "PushRate_GreenSoft", value: pushRate.GreenSoft },
		{ name: "PushRate_GreenNormal", value: pushRate.GreenNormal },
		{ name: "PushRate_GreenHard", value: pushRate.GreenHard },
		{ name: "Productivity_Red", value: productivity.Red },
		{ name: "Productivity_Orange", value: productivity.Orange },
		{ name: "Productivity_Yellow", value: productivity.Yellow },
		{ name: "Productivity_GreenSoft", value: productivity.GreenSoft },
		{ name: "Productivity_GreenNormal", value: productivity.GreenNormal },
		{ name: "Productivity_GreenHard", value: productivity.GreenHard },
		{ name: `${incentivePrefix}_Red`, value: incentive.Red },
		{ name: `${incentivePrefix}_Orange`, value: incentive.Orange },
		{ name: `${incentivePrefix}_Yellow`, value: incentive.Yellow },
		{ name: `${incentivePrefix}_GreenSoft`, value: incentive.GreenSoft },
		{ name: `${incentivePrefix}_GreenNormal`, value: incentive.GreenNormal },
		{ name: `${incentivePrefix}_GreenHard`, value: incentive.GreenHard },
		{ name: "TimeLeft_MaxValue", value: timeLeft.MaxValue },
		{ name: "TimeLeft_Red", value: timeLeft.Red },
		{ name: "TimeLeft_Orange", value: timeLeft.Orange },
		{ name: "TimeLeft_Yellow", value: timeLeft.Yellow },
		{ name: "TimeLeft_GreenSoft", value: timeLeft.GreenSoft },
		{ name: "TimeLeft_GreenNormal", value: timeLeft.GreenNormal },
		{ name: "TimeLeft_GreenHard", value: timeLeft.GreenHard },
	];

	const resp = await apiService.callV2(
		isMill1
			? "[M1].[UpsertShiftReportConfig]"
			: "[M2].[UpsertShiftReportConfig]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error saving shift report configuration");
	}
};
